import type {
  OperationProgressMetric,
  OperationProgressModel,
  OperationProgressStep,
} from '../../../../shared/ui/progress';
import { getResolvedStepCount } from './presentation';
import { isProfileApplyStepId, runningStepMessages } from './stepPlan';
import {
  buildStabilizedProfileApplyStepLabel,
  getActiveProfileApplyStep,
  isBusyProfileApplyStatus,
  isSameProfileApplyOperation,
  stabilizeProfileApplySteps,
} from './stepStabilizer';

function stabilizeProfileApplyPercent(
  previousModel: OperationProgressModel | null,
  nextModel: OperationProgressModel
): number | null | undefined {
  if (nextModel.status === 'complete') {
    return 100;
  }

  if (nextModel.status === 'error') {
    const hasItemProgressEvidence = Boolean(
      nextModel.currentItem ||
      nextModel.items?.length ||
      typeof nextModel.processed === 'number' ||
      typeof nextModel.total === 'number'
    );

    if (nextModel.percent === 100 && !hasItemProgressEvidence) {
      return null;
    }

    return nextModel.percent;
  }

  if (
    isSameProfileApplyOperation(previousModel, nextModel) &&
    typeof previousModel?.percent === 'number' &&
    typeof nextModel.percent === 'number'
  ) {
    return Math.max(previousModel.percent, nextModel.percent);
  }

  return nextModel.percent;
}

function stabilizeProfileApplyMetrics(
  metrics: OperationProgressMetric[] | undefined,
  steps: OperationProgressStep[] | undefined
): OperationProgressMetric[] | undefined {
  if (!metrics || !steps || steps.length === 0) {
    return metrics;
  }

  const resolvedStepCount = getResolvedStepCount(steps);

  return metrics.map((metric) =>
    metric.label === 'Step'
      ? {
          ...metric,
          value: `${resolvedStepCount} / ${steps.length}`,
        }
      : metric
  );
}

function shouldStabilizeProfileApplyCopy(input: {
  previousModel: OperationProgressModel | null;
  nextModel: OperationProgressModel;
  steps: OperationProgressStep[] | undefined;
}): boolean {
  if (!isSameProfileApplyOperation(input.previousModel, input.nextModel)) {
    return false;
  }

  if (!isBusyProfileApplyStatus(input.nextModel.status)) {
    return false;
  }

  const visibleActiveStep = getActiveProfileApplyStep(input.steps);
  if (!visibleActiveStep) {
    return false;
  }

  const rawActiveStep = getActiveProfileApplyStep(input.nextModel.steps);

  return rawActiveStep?.id !== visibleActiveStep.id;
}

function stabilizeProfileApplyMessage(
  previousModel: OperationProgressModel | null,
  nextModel: OperationProgressModel,
  steps: OperationProgressStep[] | undefined
): string {
  if (!shouldStabilizeProfileApplyCopy({ previousModel, nextModel, steps })) {
    return nextModel.message;
  }

  const visibleActiveStep = getActiveProfileApplyStep(steps);
  const previousActiveStep = getActiveProfileApplyStep(previousModel?.steps);

  if (
    visibleActiveStep &&
    previousActiveStep?.id === visibleActiveStep.id &&
    previousModel?.message
  ) {
    return previousModel.message;
  }

  if (visibleActiveStep && isProfileApplyStepId(visibleActiveStep.id)) {
    return runningStepMessages[visibleActiveStep.id];
  }

  return nextModel.message;
}

function stabilizeProfileApplyDetail(
  previousModel: OperationProgressModel | null,
  nextModel: OperationProgressModel,
  steps: OperationProgressStep[] | undefined
): string | undefined {
  if (!shouldStabilizeProfileApplyCopy({ previousModel, nextModel, steps })) {
    return nextModel.detail;
  }

  const visibleActiveStep = getActiveProfileApplyStep(steps);
  const previousActiveStep = getActiveProfileApplyStep(previousModel?.steps);

  if (
    visibleActiveStep &&
    previousActiveStep?.id === visibleActiveStep.id &&
    previousModel?.detail
  ) {
    return previousModel.detail;
  }

  return visibleActiveStep?.detail ?? nextModel.detail;
}

export function stabilizeProfileApplyWindowModel(
  previousModel: OperationProgressModel | null,
  nextModel: OperationProgressModel | null
): OperationProgressModel | null {
  if (!nextModel) {
    return null;
  }

  const steps = stabilizeProfileApplySteps(previousModel, nextModel);

  return {
    ...nextModel,
    message: stabilizeProfileApplyMessage(previousModel, nextModel, steps),
    detail: stabilizeProfileApplyDetail(previousModel, nextModel, steps),
    percent: stabilizeProfileApplyPercent(previousModel, nextModel),
    stepLabel: buildStabilizedProfileApplyStepLabel(steps, nextModel.stepLabel),
    steps,
    metrics: stabilizeProfileApplyMetrics(nextModel.metrics, steps),
  };
}
